import {
  type Signal,
  type SignalMetadataSchema,
  type SignalMetadataSchemaField,
  isValidMetadataField,
} from "@repo/app-types/entities/signal";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";

/** Returns true if the signal form has unsaved modifications. */
export function isSignalFormDirty(
  savedSignal: Signal | undefined,
  name: string,
  description: string,
  metadataSchema: SignalMetadataSchema,
): boolean {
  if (!savedSignal) {
    // New signal: dirty if the user has typed anything
    return (
      name.trim() !== "" ||
      description.trim() !== "" ||
      metadataSchema.fields.length > 0
    );
  }
  return (
    name !== savedSignal.name ||
    description !== savedSignal.description ||
    JSON.stringify(metadataSchema) !==
      JSON.stringify(savedSignal.metadata_schema)
  );
}

/**
 * Filter out empty keys and empty enum options from metadata schema fields.
 * Preserves each field's stable `id` so edits to the display label don't
 * invalidate previously extracted metadata.
 */
export function cleanMetadataSchema(
  schema: SignalMetadataSchema,
): SignalMetadataSchema {
  const cleaned = schema.fields
    .filter((f) => f.key.trim() !== "")
    .flatMap((f): SignalMetadataSchemaField[] => {
      switch (f.type) {
        case "enum":
          return [{ ...f, options: f.options.filter((o) => o.trim() !== "") }];
        case "number":
          return [f];
        default:
          return assertUnreachable(f, () => []);
      }
    })
    .filter(isValidMetadataField);
  return { fields: cleaned };
}
